import { Button } from '@/shared/components/ui/Button/Button';
import { CANCELLABLE_STAGES, useUploadQueue } from '../state/uploadQueue';
import type { UploadItem } from '../state/uploadQueue';

function percentOf(item: UploadItem): number {
  return item.totalBytes === 0 ? 0 : Math.round((item.sentBytes / item.totalBytes) * 100);
}

function stageLabel(item: UploadItem): string {
  switch (item.stage) {
    case 'queued':
      return 'Waiting';
    case 'uploading':
      return `Uploading, ${percentOf(item)}%`;
    case 'completing':
      return 'Finishing the upload';
    case 'creating':
      return 'Creating the document';
    case 'verifying':
      return 'Checking the file';
    case 'done':
      return item.stillVerifying ? 'Uploaded; the file is still being checked' : 'Uploaded';
    case 'failed':
      return 'Failed';
  }
}

/** One line for screen readers, rewritten as uploads move on so each change is announced. */
function summaryOf(items: readonly UploadItem[]): string {
  const count = (test: (item: UploadItem) => boolean): number => items.filter(test).length;
  const running = count((item) => item.stage !== 'done' && item.stage !== 'failed');
  const done = count((item) => item.stage === 'done');
  const failed = count((item) => item.stage === 'failed');
  return [
    running > 0 ? `${running} uploading` : null,
    done > 0 ? `${done} uploaded` : null,
    failed > 0 ? `${failed} failed` : null,
  ]
    .filter((part) => part !== null)
    .join(', ');
}

/** Progress of the app's uploads, wherever the person is in the app. */
export function UploadTray(): React.JSX.Element {
  const { queue, items } = useUploadQueue();

  return (
    <>
      {/* Rendered before the first upload, so the first change is announced too. */}
      <p className="visually-hidden" aria-live="polite">
        {summaryOf(items)}
      </p>
      {items.length === 0 ? null : (
        <section className="upload-tray" aria-label="Uploads">
          <h2 className="upload-tray__title">Uploads</h2>
          <ul className="upload-tray__list">
            {items.map((item) => (
              <li key={item.id} className="upload-tray__item" data-testid="upload-tray-item">
                <div className="upload-tray__row">
                  <span className="upload-tray__name">{item.title}</span>
                  {CANCELLABLE_STAGES.has(item.stage) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={() => queue.cancel(item.id)}
                      accessibilityLabel={`Cancel ${item.title}`}
                    >
                      Cancel
                    </Button>
                  ) : item.stage === 'done' || item.stage === 'failed' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={() => queue.dismiss(item.id)}
                      accessibilityLabel={`Dismiss ${item.title}`}
                    >
                      Dismiss
                    </Button>
                  ) : null}
                </div>
                <span className={`upload-tray__stage upload-tray__stage--${item.stage}`}>
                  {stageLabel(item)}
                </span>
                {item.stage === 'uploading' ? (
                  <div
                    className="upload-progress"
                    role="progressbar"
                    aria-label={`Uploading ${item.title}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percentOf(item)}
                  >
                    <div className="upload-progress__bar" style={{ width: `${percentOf(item)}%` }} />
                  </div>
                ) : null}
                {item.error === null ? null : <p className="upload-tray__error">{item.error}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
